import { useState, useMemo } from 'react';
import {
    Card,
    CardContent,
    Button,
    Badge,
    Input,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    Label,
    Switch,
} from '@ui';
import { cn } from '@ui/lib/utils';
import {
    Plus,
    Activity,
    Edit,
    Trash2,
    Search,
    AlertCircle,
    Coins,
} from 'lucide-react';
import { useGroupStore } from '@/stores/groupStore';
import { useGetActivityTypeList, useCreateActivityType, useUpdateActivityType, useDeleteActivityType, useToggleActivityType } from '@hooks/generated/group';
import { toastMessage } from '@ui/components/Toast';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// Activity Type 인터페이스 (API 응답 타입)
interface ActivityType {
    id: number;
    name: string;
    type?: string;
    description?: string;
    color?: string;
    isActive: boolean;
    usageCount?: number;
    defaultAmount?: number;
    createdAt?: string;
    updatedAt?: string;
}

// API 에러 메시지 추출 헬퍼
const getApiErrorMessage = (error: unknown, fallbackMessage: string): string => {
    if (error instanceof AxiosError) {
        const responseMessage = error.response?.data?.message;
        if (responseMessage) {
            return responseMessage;
        }
    }
    if (error instanceof Error) {
        return error.message || fallbackMessage;
    }
    return fallbackMessage;
};

export function ActivityTab() {
    const selectedGroup = useGroupStore((state) => state.selectedGroup);
    const groupId = selectedGroup?.id;
    const queryClient = useQueryClient();

    // 검색 상태
    const [searchQuery, setSearchQuery] = useState('');

    // 모달 상태
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState({ name: '', type: 'GENERAL', description: '', defaultAmount: '' });

    // 삭제 확인 상태
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

    // 활동 타입 목록 조회
    const { data: activityTypesData, isLoading } = useGetActivityTypeList({
        request: { groupId: groupId || 0, includeInactive: true },
        options: { enabled: !!groupId },
    });

    const activityTypes: ActivityType[] = useMemo(() => {
        if (!activityTypesData || !Array.isArray(activityTypesData)) return [];
        return activityTypesData as ActivityType[];
    }, [activityTypesData]);

    // 필터링된 활동 타입
    const filteredActivityTypes = useMemo(() => {
        if (!searchQuery) return activityTypes;
        const query = searchQuery.toLowerCase();
        return activityTypes.filter(type =>
            type.name.toLowerCase().includes(query) ||
            type.description?.toLowerCase().includes(query)
        );
    }, [activityTypes, searchQuery]);

    // Mutations
    const createMutation = useCreateActivityType({
        options: {
            onSuccess: () => {
                toastMessage({ message: '활동 타입이 생성되었습니다.', type: 'success' });
                queryClient.invalidateQueries({ queryKey: ['getActivityTypeList'] });
                closeModal();
            },
            onError: (error) => {
                toastMessage({ message: getApiErrorMessage(error, '활동 타입 생성에 실패했습니다.'), type: 'error' });
            },
        },
    });

    const updateMutation = useUpdateActivityType({
        options: {
            onSuccess: () => {
                toastMessage({ message: '활동 타입이 수정되었습니다.', type: 'success' });
                queryClient.invalidateQueries({ queryKey: ['getActivityTypeList'] });
                closeModal();
            },
            onError: (error) => {
                toastMessage({ message: getApiErrorMessage(error, '활동 타입 수정에 실패했습니다.'), type: 'error' });
            },
        },
    });

    const deleteMutation = useDeleteActivityType({
        options: {
            onSuccess: () => {
                toastMessage({ message: '활동 타입이 삭제되었습니다.', type: 'success' });
                queryClient.invalidateQueries({ queryKey: ['getActivityTypeList'] });
                setDeleteConfirmId(null);
            },
            onError: (error) => {
                toastMessage({ message: getApiErrorMessage(error, '활동 타입 삭제에 실패했습니다.'), type: 'error' });
            },
        },
    });

    const toggleMutation = useToggleActivityType({
        options: {
            onSuccess: () => {
                toastMessage({ message: '활동 타입 상태가 변경되었습니다.', type: 'success' });
                queryClient.invalidateQueries({ queryKey: ['getActivityTypeList'] });
            },
            onError: (error) => {
                toastMessage({ message: getApiErrorMessage(error, '활동 타입 상태 변경에 실패했습니다.'), type: 'error' });
            },
        },
    });

    // 모달 핸들러
    const openCreateModal = () => {
        setModalMode('create');
        setEditingId(null);
        setFormData({ name: '', type: 'GENERAL', description: '', defaultAmount: '' });
        setIsModalOpen(true);
    };

    const openEditModal = (type: ActivityType) => {
        setModalMode('edit');
        setEditingId(type.id);
        setFormData({
            name: type.name,
            type: type.type || 'GENERAL',
            description: type.description || '',
            defaultAmount: type.defaultAmount !== undefined && type.defaultAmount !== null ? type.defaultAmount.toString() : '',
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingId(null);
        setFormData({ name: '', type: 'GENERAL', description: '', defaultAmount: '' });
    };

    // API 핸들러
    const handleSubmit = () => {
        if (!groupId || !formData.name.trim()) {
            toastMessage({ message: '활동 타입 이름을 입력해주세요.', type: 'error' });
            return;
        }

        const priceValue = formData.defaultAmount ? parseInt(formData.defaultAmount, 10) : undefined;

        if (modalMode === 'create') {
            // API 스펙에 맞춰 타입 단언 사용
            createMutation.mutate({
                groupId,
                groupActivityTypeCreateRequest: {
                    name: formData.name.trim(),
                    type: formData.type as 'HOURLY',
                    description: formData.description?.trim() || undefined,
                    defaultAmount: priceValue,
                } as Parameters<typeof createMutation.mutate>[0]['groupActivityTypeCreateRequest'],
            });
        } else if (editingId) {
            // API 스펙에 맞춰 타입 단언 사용
            updateMutation.mutate({
                groupId,
                typeId: editingId,
                groupActivityTypeUpdateRequest: {
                    name: formData.name.trim(),
                    description: formData.description?.trim() || undefined,
                    defaultAmount: priceValue,
                } as Parameters<typeof updateMutation.mutate>[0]['groupActivityTypeUpdateRequest'],
            });
        }
    };

    const handleDelete = (typeId: number) => {
        if (!groupId) return;
        deleteMutation.mutate({ groupId, typeId });
    };

    const handleToggle = (typeId: number) => {
        if (!groupId) return;
        toggleMutation.mutate({ groupId, typeId });
    };

    // 통계
    const stats = useMemo(() => ({
        total: activityTypes.length,
        active: activityTypes.filter(t => t.isActive).length,
        inactive: activityTypes.filter(t => !t.isActive).length,
    }), [activityTypes]);

    const isPending = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="space-y-6">
            {/* 헤더 */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100 flex items-center gap-2">
                            <Activity className="h-5 w-5 text-orange-500" />
                            활동 타입 관리
                        </h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                            지점에서 사용할 활동 타입을 관리합니다.
                            <span className="ml-2 text-neutral-400 dark:text-neutral-500">
                                (전체 {stats.total}개 / 활성 {stats.active}개 / 비활성 {stats.inactive}개)
                            </span>
                        </p>
                    </div>
                    <Button className="gap-2" onClick={openCreateModal}>
                        <Plus className="h-4 w-4" />
                        새 활동 타입 추가
                    </Button>
                </div>

                {/* 검색 */}
                <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 dark:text-neutral-500" />
                    <Input
                        placeholder="활동 타입 검색..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                    />
                </div>
            </div>

            {/* 활동 타입 목록 */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-orange-500 border-t-transparent" />
                </div>
            ) : filteredActivityTypes.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Activity className="mx-auto h-12 w-12 text-neutral-300" />
                        <p className="mt-4 text-neutral-500 dark:text-neutral-400">
                            {searchQuery ? '검색 결과가 없습니다.' : '등록된 활동 타입이 없습니다.'}
                        </p>
                        {!searchQuery && (
                            <Button className="mt-4 gap-2" onClick={openCreateModal}>
                                <Plus className="h-4 w-4" />
                                첫 번째 활동 타입 만들기
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-3">
                    {filteredActivityTypes.map((type) => (
                        <Card
                            key={type.id}
                            className={cn(
                                'transition-all',
                                !type.isActive && 'opacity-60 bg-neutral-50 dark:bg-neutral-800/50'
                            )}
                        >
                            <CardContent className="p-4">
                                {deleteConfirmId === type.id ? (
                                    // 삭제 확인 모드
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-danger-600">
                                            <AlertCircle className="h-5 w-5" />
                                            <span className="font-medium">'{type.name}' 활동 타입을 삭제하시겠습니까?</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() => handleDelete(type.id)}
                                                disabled={deleteMutation.isPending}
                                            >
                                                삭제
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setDeleteConfirmId(null)}
                                            >
                                                취소
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    // 보기 모드
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                'flex h-10 w-10 items-center justify-center rounded-lg',
                                                type.isActive ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-neutral-100 dark:bg-neutral-800'
                                            )}>
                                                <Activity className={cn(
                                                    'h-5 w-5',
                                                    type.isActive ? 'text-orange-600' : 'text-neutral-400 dark:text-neutral-500'
                                                )} />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-medium text-neutral-800 dark:text-neutral-100">{type.name}</h3>
                                                    <Badge variant={type.isActive ? 'default' : 'secondary'} className={cn(
                                                        'text-xs',
                                                        type.isActive && 'bg-green-500'
                                                    )}>
                                                        {type.isActive ? '활성' : '비활성'}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
                                                    {type.description && (
                                                        <span>{type.description}</span>
                                                    )}
                                                    {type.description && (type.usageCount !== undefined || type.defaultAmount) && (
                                                        <span className="text-neutral-300">|</span>
                                                    )}
                                                    {type.defaultAmount !== undefined && type.defaultAmount !== null && type.defaultAmount > 0 && (
                                                        <span className="flex items-center gap-1 text-orange-600 font-medium">
                                                            <Coins className="h-3 w-3" />
                                                            {type.defaultAmount.toLocaleString()}원
                                                        </span>
                                                    )}
                                                    {type.defaultAmount !== undefined && type.defaultAmount > 0 && type.usageCount !== undefined && (
                                                        <span className="text-neutral-300">|</span>
                                                    )}
                                                    {type.usageCount !== undefined && (
                                                        <span>사용 {type.usageCount}회</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Switch
                                                checked={type.isActive}
                                                onCheckedChange={() => handleToggle(type.id)}
                                                disabled={toggleMutation.isPending}
                                            />
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => openEditModal(type)}
                                                title="수정"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-danger-500 hover:text-danger-600"
                                                onClick={() => setDeleteConfirmId(type.id)}
                                                title="삭제"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* 활동 타입 추가/수정 모달 */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {modalMode === 'create' ? '새 활동 타입 추가' : '활동 타입 수정'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="activity-name">활동 타입 이름 *</Label>
                            <Input
                                id="activity-name"
                                placeholder="예: 산책, 식사, 놀이"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                autoFocus
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="activity-description">설명 (선택사항)</Label>
                            <Input
                                id="activity-description"
                                placeholder="활동 타입에 대한 설명을 입력하세요"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="activity-price">금액 (선택사항)</Label>
                            <div className="relative">
                                <Coins className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 dark:text-neutral-500" />
                                <Input
                                    id="activity-price"
                                    type="number"
                                    placeholder="0"
                                    value={formData.defaultAmount}
                                    onChange={(e) => setFormData(prev => ({ ...prev, defaultAmount: e.target.value }))}
                                    className="pl-9"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400 dark:text-neutral-500">원</span>
                            </div>
                            <p className="text-xs text-neutral-400 dark:text-neutral-500">활동 템플릿에서 최종 금액 계산 시 사용됩니다.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeModal} disabled={isPending}>
                            취소
                        </Button>
                        <Button onClick={handleSubmit} disabled={isPending || !formData.name.trim()}>
                            {isPending ? '저장 중...' : modalMode === 'create' ? '추가' : '저장'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
